import CLASSNAME from "./classname";
import SIZE from "./size";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { toggleAudio, updateLiveStreamSettings, updateSessionSettings } from "../../../../store/reducers/livestreamSettings";
import { RootState } from "../../../../store";
import { useEffect, useMemo, useState } from "react";
import RoomClient from "components/containers/utils/mediaSoup/RoomClient";

interface AudioProps {
  roomClient: RoomClient | null;
}


const AudioControl = ({ roomClient }: AudioProps) => {
  const settings = useSelector((state: RootState) => state.live.settings);
  const producers = useSelector((state: RootState) => state.live.settings.producers);
  const dispatch = useDispatch();
  const sessionSettingData = useSelector((state: any) => state?.live?.settings?.sessionSettingData);
  useEffect(() => {
    if (sessionSettingData?.forceMute || sessionSettingData?.systemMute) {
      dispatch(updateLiveStreamSettings({ audio: false }));
    }
  }, [sessionSettingData?.forceMute, sessionSettingData?.systemMute]);

  const [micState, setMicState] = useState<'on' | 'off' | 'unsupported'>('unsupported');
  const [systemForceMuted, setSystemForceMuted] = useState(false);
  const [systemMuted, setSystemMuted] = useState(false);
  const [webcamState, setWebcamState] = useState<'on' | 'off'>('off');
  const audioProducer: any = useMemo(() => {
    return Object.values(producers).find((p: any) => p.kind === "audio");
  }, [producers]);

  // Sync local mic state
  useEffect(() => {
    if (!audioProducer) {
      setMicState("unsupported");
    } else if (!audioProducer.paused) {
      setMicState("on");
    } else {
      setMicState("off");
    }
  }, [audioProducer]);

  // 🔇 Automatically mute when force/system mute is triggered
  useEffect(() => {
    const { forceMute, systemMute } = sessionSettingData || {};

    setSystemForceMuted(!!forceMute);
    setSystemMuted(!!systemMute);

    if (forceMute || systemMute) {
      roomClient?.muteMic();
      dispatch(updateLiveStreamSettings({ audio: false }));

      const micState = systemMute ? "systemMuted" : "forceMuted";
      dispatch(updateSessionSettings({ micState }));
    }
  }, [sessionSettingData?.forceMute, sessionSettingData?.systemMute]);


  const toggleMic = () => {
    if (systemForceMuted) return; // 🚫 can't unmute when force muted

    const micCurrentlyOn = settings.audio;

    if (!micCurrentlyOn) {
      // User is trying to unmute
      roomClient?.unmuteMic();
      dispatch(updateLiveStreamSettings({ audio: true }));
      dispatch(updateSessionSettings({ systemMute: false, micState: 'enabled' }));
    } else {
      // User is trying to mute
      roomClient?.muteMic();
      dispatch(updateLiveStreamSettings({ audio: false }));
      dispatch(updateSessionSettings({ micState: "disabled" }));
    }
  };


  return (
    <div className="relative group w-12vw h-12vw">
      <button onClick={toggleMic}
        className={`${CLASSNAME} ${!settings.audio ? "bg-red-600" : ""
          } p-2 rounded ${systemForceMuted ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {micState === 'on' || settings.audio ? <FaMicrophone /> : <FaMicrophoneSlash />}
      </button>

      <div className="mobile:hidden absolute top-full mt-2 left-1/2 transform -translate-x-1/2 w-max bg-black text-white text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {systemForceMuted
          ? "Mic disabled by Trainer"
          : systemMuted && !settings.audio
            ? "Mic muted by system, you can re-enable"
            : systemMuted && settings.audio
              ? "Mic re-enabled (system override active)"
              : settings.audio
                ? "Mic on"
                : "Mic off"}
      </div>
    </div>
  );
};

export default AudioControl;
